import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MOCK_WEEK, MOCK_DAILY_GOAL } from '../mock';
import { TrendingUp, TrendingDown, Flame, Award } from 'lucide-react';
import Seo from '../components/Seo';

const History = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && !user) navigate('/'); }, [user, loading, navigate]);

  const max = Math.max(...MOCK_WEEK.map(w => w.calories), MOCK_DAILY_GOAL.calories);
  const avg = Math.round(MOCK_WEEK.reduce((a,b) => a + b.calories, 0) / MOCK_WEEK.length);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#fefdf9]">
      <Seo title="Analytics & streaks — CalorieSnap" description="Weekly rhythm, streaks and progress." canonical="https://www.thecaloriesnap.com/app/history" />
      <Navbar variant="app" />
      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="mb-8">
          <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-1">Analytics</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-emerald-950">Your weekly rhythm</h1>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Flame, color: 'orange', label: 'Streak', value: '14 days' },
            { icon: TrendingUp, color: 'emerald', label: 'Avg / day', value: `${avg} kcal` },
            { icon: TrendingDown, color: 'blue', label: 'Weight change', value: '-2.1 kg' },
            { icon: Award, color: 'yellow', label: 'Best week', value: '7/7 days' },
          ].map(s => (
            <Card key={s.label} className="p-5 rounded-2xl border-emerald-950/5 bg-white flex items-center gap-4">
              <div className={`w-11 h-11 rounded-xl bg-${s.color}-100 text-${s.color}-600 grid place-items-center`}><s.icon className="w-5 h-5" /></div>
              <div><div className="font-display text-lg font-bold text-emerald-950">{s.value}</div><div className="text-xs text-emerald-950/60">{s.label}</div></div>
            </Card>
          ))}
        </div>

        <Card className="p-8 rounded-3xl border-emerald-950/5 bg-white">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl font-bold text-emerald-950">This week</h3>
            <span className="text-xs text-emerald-950/50">Goal: {MOCK_DAILY_GOAL.calories} kcal / day</span>
          </div>
          <div className="flex items-end gap-3 h-64">
            {MOCK_WEEK.map(d => {
              const h = (d.calories / max) * 100;
              const overGoal = d.calories > MOCK_DAILY_GOAL.calories;
              return (
                <div key={d.day} className="flex-1 h-full flex flex-col items-center justify-end gap-2">
                  <div className={`w-full rounded-t-xl transition-all duration-700 ${overGoal ? 'bg-orange-400' : 'bg-emerald-500'}`} style={{ height: `${h}%` }} />
                  <div className="text-xs font-medium text-emerald-950">{d.calories}</div>
                  <div className="text-xs text-emerald-950/50">{d.day}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-6 border-t border-emerald-950/5 flex items-center gap-6 text-xs text-emerald-950/60">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-500" />On target</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-orange-400" />Over goal</div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default History;
